import React, { useState } from "react";
import { BookOpen, ChevronDown, ChevronRight, Package, ShoppingBag, Factory, Warehouse, ShoppingCart, FileSearch, Droplets, BarChart2 } from "lucide-react";

const guides = [
  {
    id: "alta-producto",
    icon: Package,
    color: "text-blue-600 bg-blue-50",
    title: "1. Dar de alta un producto terminado",
    content: `**Ruta:** Menu lateral > Productos > + Nuevo Producto

**Pasos:**
1. **Nombre:** Escribe el nombre (ej: "Pulmon de cerdo deshidratado")
2. **Categoria:** Selecciona "Producto Final"
3. **Unidad de venta:** kg o pza (como lo vendes)
4. **Unidad de compra:** kg (como compras la materia prima)

**Configurar produccion:**
- Activa "Se puede producir/fabricar"
- **Cantidad minima por lote:** Ej: 10 kg crudo
- **Rendimiento:** Ej: 25% (de 10 kg crudo salen 2.5 kg deshidratado)
- **Merma corte:** Ej: 5% (perdida al cortar)
- **Merma horno:** Ej: 70% (perdida por deshidratacion)

**Receta / Materias Primas:**
- Agrega "Pulmon de cerdo crudo" como ingrediente
- Cantidad: 4 kg por cada 1 kg de producto terminado
- Esto significa: para hacer 1 kg de pulmon deshidratado necesitas 4 kg de crudo

**Costos tipicos por lote:**
- Horas tipicas en horno: 28 (ejemplo)
- Agrega costos: Gas ($500), Electricidad ($200), Mano de obra ($300)
- Estos se auto-rellenan cada vez que creas un lote

**Guardar** y listo. El producto queda configurado.`
  },
  {
    id: "materia-prima",
    icon: Package,
    color: "text-amber-600 bg-amber-50",
    title: "2. Dar de alta materia prima",
    content: `**Ruta:** Menu lateral > Productos > + Nuevo Producto

**Pasos:**
1. **Nombre:** "Pulmon de cerdo crudo"
2. **Categoria:** Selecciona "Materia Prima"
3. **Unidad:** kg
4. **Costo de compra:** Precio por kg que pagas al proveedor
5. **Stock minimo:** Cantidad minima que quieres tener siempre

Repite para cada materia prima (higado crudo, traquea cruda, etc.)`
  },
  {
    id: "compra",
    icon: ShoppingBag,
    color: "text-purple-600 bg-purple-50",
    title: "3. Registrar una compra de materia prima",
    content: `**Ruta:** Menu lateral > Compras > + Nueva Compra

**Pasos:**
1. Selecciona el **proveedor**
2. Agrega los productos comprados (ej: Pulmon crudo, 50 kg, $45/kg)
3. Si compras en kg pero vendes en pzas, el sistema calcula automaticamente
4. **Guardar** la compra

**Despues de guardar:**
- Ve a **Recepciones** para registrar cuando llega la mercancia
- En la recepcion puedes registrar:
  - Peso comprado vs peso real recibido
  - Lote del proveedor
  - Estado de la mercancia
- El inventario se actualiza automaticamente`
  },
  {
    id: "produccion",
    icon: Factory,
    color: "text-orange-600 bg-orange-50",
    title: "4. Crear un lote de produccion",
    content: `**Ruta:** Menu lateral > Fabricacion > + Nuevo Lote

**Pasos:**
1. Selecciona el **horno** a usar
2. Agrega el **producto** a fabricar (ej: Pulmon de cerdo deshidratado)
3. Ingresa la **cantidad de entrada** (kg de crudo que vas a meter)
4. El sistema auto-calcula la salida estimada segun el rendimiento del producto

**Trazabilidad (SENASICA):**
5. En la seccion "Trazabilidad - Materia Prima", vincula las **recepciones** de donde viene el crudo
6. El numero de lote se genera automaticamente: PULMON-20260315-001

**Costos:**
7. Los costos tipicos del producto se auto-rellenan (gas, luz, mano de obra)
8. Puedes ajustarlos si este lote es diferente

**Gas:**
9. Si usas cilindros de gas, registra el consumo: peso antes y despues de conectar

**Guardar** y el lote queda en estado "Programado".`
  },
  {
    id: "completar-lote",
    icon: Factory,
    color: "text-green-600 bg-green-50",
    title: "5. Completar un lote (registrar lo real)",
    content: `**Ruta:** Menu lateral > Fabricacion > Click en el lote > Editar

**Al terminar la produccion:**
1. En "Control de Mermas y Rendimiento":
   - **Peso comprado:** Lo que compraste originalmente
   - **Peso real sin empaque:** Despues de quitar bolsas/cajas
   - **Peso post-corte:** Despues de cortar y limpiar
   - **Salida real:** Kg de producto terminado final

2. En "Estimado vs Real":
   - El sistema muestra lo que ESTIMASTE gastar en gas, luz, etc.
   - Tu pones lo que REALMENTE gastaste
   - Te muestra la diferencia en verde (ahorraste) o rojo (gastaste mas)

3. Cambia el estado a **"Completado"**
4. **Guardar**

**Al completar:**
- El inventario de producto terminado aumenta automaticamente
- El inventario de materia prima se descuenta automaticamente
- El sistema te dice si hay pedidos pendientes que ya puedes surtir`
  },
  {
    id: "venta",
    icon: ShoppingCart,
    color: "text-indigo-600 bg-indigo-50",
    title: "6. Registrar una venta",
    content: `**Ruta:** Menu lateral > Ventas > + Nueva Venta

**Pasos:**
1. Selecciona el **tipo:** Pedido, Cotizacion, Factura o Ticket
2. Selecciona el **cliente** (o deja vacio para venta rapida)
3. **Canal de venta:** Directo, Redes sociales, Expo, Menudeo, etc.
4. Agrega **productos:**
   - Selecciona el producto
   - **Presentacion:** Ej: "Bolsa 100g", "Bolsa 500g"
   - Cantidad, precio unitario
5. Forma de pago
6. **Guardar**

**El inventario se descuenta automaticamente al confirmar la venta.**`
  },
  {
    id: "trazabilidad",
    icon: FileSearch,
    color: "text-cyan-600 bg-cyan-50",
    title: "7. Consultar trazabilidad (SENASICA)",
    content: `**Ruta:** Menu lateral > Trazabilidad

**Busqueda:**
- Escribe un numero de lote, folio de recepcion, nombre de proveedor o folio de venta

**3 tipos de rastreo:**

1. **Hacia adelante (Recepcion → Venta):**
   - Selecciona una recepcion de materia prima
   - Ve en que lotes de produccion se uso
   - Ve a que clientes se vendio

2. **Hacia atras (Venta → Recepcion):**
   - Selecciona una venta/pedido
   - Ve de que lotes de produccion viene
   - Ve de que proveedor y recepcion viene la materia prima

3. **Por lote:**
   - Selecciona un lote de produccion
   - Ve toda la cadena completa + registros de limpieza

**Imprimir reportes:** Click en "Imprimir / PDF" para generar reporte imprimible para auditorias SENASICA.`
  },
  {
    id: "limpieza",
    icon: Droplets,
    color: "text-teal-600 bg-teal-50",
    title: "8. Registrar limpieza (SENASICA)",
    content: `**Ruta:** Menu lateral > Limpieza > + Nuevo Registro

**Pasos:**
1. **Fecha y hora** de la limpieza
2. **Area:** Horno, Mesa de trabajo, Utensilios, Cochera, Almacen, Empaque
3. **Producto de limpieza:** Ej: "Cloro al 5%", "Jabon biodegradable"
4. **Responsable:** Tu nombre o quien hizo la limpieza
5. **Observaciones:** Notas adicionales

**Importante:** SENASICA requiere evidencia de limpieza antes y despues de cada produccion. Los registros de limpieza se vinculan automaticamente a los lotes de produccion por fecha.`
  },
  {
    id: "flujo-completo",
    icon: BarChart2,
    color: "text-rose-600 bg-rose-50",
    title: "Flujo completo (de compra a venta)",
    content: `**El flujo completo en tu negocio:**

**PASO 1 - Compra:**
Compras > + Nueva Compra > Registras 50kg de pulmon crudo a $45/kg

**PASO 2 - Recepcion:**
Recepciones > Registras que llego, peso real, lote del proveedor

**PASO 3 - Produccion:**
Fabricacion > + Nuevo Lote > Seleccionas pulmon deshidratado
- Vinculas la recepcion (trazabilidad)
- Se auto-rellenan costos estimados
- Registras gas, tiempos de horno

**PASO 4 - Completar lote:**
Editas el lote > Registras pesos reales y costos reales
- Cambias estado a "Completado"
- Inventario de PT aumenta, inventario de MP baja

**PASO 5 - Venta:**
Ventas > + Nueva Venta > Seleccionas producto, presentacion, canal

**PASO 6 - Verificar:**
Trazabilidad > Buscas el lote o la venta > Ves toda la cadena
- Imprimes el reporte PDF para SENASICA

**Dashboard:** Ve el resumen de todo: produccion, ventas, costos, rendimientos.`
  },
];

export default function Guia() {
  const [open, setOpen] = useState(new Set(["flujo-completo"]));

  const toggle = (id) => {
    const next = new Set(open);
    if (next.has(id)) next.delete(id); else next.add(id);
    setOpen(next);
  };

  return (
    <div className="space-y-4" data-testid="guia-page">
      <div>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <BookOpen size={20} /> Guia de Uso
        </h1>
        <p className="text-xs text-slate-500 mt-1">Paso a paso para usar todo el sistema. Click en cada seccion para expandir.</p>
      </div>

      <div className="space-y-2">
        {guides.map(g => (
          <div key={g.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`guide-${g.id}`}>
            <button onClick={() => toggle(g.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
              <div className={`p-1.5 rounded-lg ${g.color}`}><g.icon size={16} /></div>
              <span className="flex-1 text-sm font-medium text-slate-800">{g.title}</span>
              {open.has(g.id) ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
            </button>
            {open.has(g.id) && (
              <div className="px-4 pb-4 border-t border-slate-100">
                <div className="prose prose-xs max-w-none mt-3 text-slate-600 text-xs leading-relaxed whitespace-pre-line">
                  {g.content.split("\n").map((line, i) => {
                    if (line.startsWith("**") && line.endsWith("**")) {
                      return <div key={i} className="font-bold text-slate-800 mt-2 mb-0.5">{line.replace(/\*\*/g, "")}</div>;
                    }
                    if (line.match(/^\*\*.+\*\*:/)) {
                      const parts = line.match(/^\*\*(.+?)\*\*:(.*)$/);
                      return <div key={i} className="mt-1"><span className="font-bold text-slate-700">{parts?.[1]}:</span>{parts?.[2]}</div>;
                    }
                    if (line.match(/^\d+\./)) {
                      return <div key={i} className="ml-2 mt-0.5">{line}</div>;
                    }
                    if (line.startsWith("- ")) {
                      return <div key={i} className="ml-4 mt-0.5 text-slate-500">{line}</div>;
                    }
                    if (line.trim() === "") return <div key={i} className="h-1" />;
                    return <div key={i}>{line}</div>;
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
